<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-8">

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-2 text-muted">Loading profile...</p>
        </div>

        <template v-else-if="profile">
          <!-- Profile Info Card -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-body p-4">
              <div class="d-flex align-items-center mb-4">
                <div class="avatar-circle me-4">
                  <span class="avatar-initials">{{ initials }}</span>
                </div>
                <div>
                  <h4 class="fw-bold mb-1">{{ profile.name }}</h4>
                  <span class="badge bg-primary-subtle text-primary">{{ profile.customerRank?.name || 'N/A' }}</span>
                </div>
              </div>

              <div class="row g-3">
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">Email</label>
                  <p class="mb-0 fw-medium">{{ profile.email || '—' }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">Phone</label>
                  <p class="mb-0 fw-medium">{{ profile.phone || '—' }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">Alias</label>
                  <p class="mb-0 fw-medium">{{ profile.alias || '—' }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">Balance</label>
                  <p class="mb-0 fw-medium text-success fw-bold">{{ Number(profile.balance).toLocaleString() }} VND</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Extended Profile -->
          <div class="card border-0 shadow-sm mb-4" v-if="profile.profile">
            <div class="card-header bg-white fw-bold border-bottom">Profile Details</div>
            <div class="card-body p-4">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">Full Name</label>
                  <p class="mb-0 fw-medium">{{ profile.profile.full_name || '—' }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">Gender</label>
                  <p class="mb-0 fw-medium">{{ profile.profile.gender || '—' }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">Birthday</label>
                  <p class="mb-0 fw-medium">{{ profile.profile.birthday || '—' }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">City</label>
                  <p class="mb-0 fw-medium">{{ profile.profile.city || '—' }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">State</label>
                  <p class="mb-0 fw-medium">{{ profile.profile.state || '—' }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted fw-semibold">Zipcode</label>
                  <p class="mb-0 fw-medium">{{ profile.profile.zipcode || '—' }}</p>
                </div>
                <div class="col-12">
                  <label class="small text-muted fw-semibold">Address</label>
                  <p class="mb-0 fw-medium">{{ profile.profile.address || '—' }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Update Form -->
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white fw-bold border-bottom">Update Account</div>
            <div class="card-body p-4">
              <form @submit.prevent="handleUpdate">
                <div class="mb-3">
                  <label class="form-label fw-semibold">Name</label>
                  <input type="text" class="form-control" v-model="updateForm.name" placeholder="Enter name" required>
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold">New Password</label>
                  <input type="password" class="form-control" v-model="updateForm.password" placeholder="Leave blank to keep current">
                </div>
                <template v-if="updateForm.password">
                  <div class="mb-3">
                    <label class="form-label fw-semibold">Old Password <span class="text-danger">*</span></label>
                    <input type="password" class="form-control" v-model="updateForm.old_password" placeholder="Enter current password">
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-semibold">Confirm New Password <span class="text-danger">*</span></label>
                    <input type="password" class="form-control" v-model="updateForm.password_confirmation" placeholder="Repeat new password">
                  </div>
                </template>
                <div v-if="updateError" class="alert alert-danger py-2 small">{{ updateError }}</div>
                <button type="submit" class="btn btn-primary px-4" :disabled="updating">
                  <span v-if="updating" class="spinner-border spinner-border-sm me-1"></span>
                  {{ updating ? 'Saving...' : 'Save Changes' }}
                </button>
              </form>
            </div>
          </div>
        </template>

        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useUser } from '@/composables/useUser'
import { createToaster } from '@meforma/vue-toaster'

const toaster = createToaster({ position: 'top-right', duration: 2000 })
const { getMyProfileApi, updateMyProfileApi } = useUser()

const profile = ref(null)
const loading = ref(false)
const error = ref('')
const updating = ref(false)
const updateError = ref('')

const updateForm = reactive({
  name: '',
  password: '',
  old_password: '',
  password_confirmation: ''
})

const initials = computed(() => {
  if (!profile.value?.name) return '?'
  return profile.value.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
})

const fetchProfile = async () => {
  loading.value = true
  error.value = ''
  const res = await getMyProfileApi()
  if (res.success) {
    profile.value = res.data
    updateForm.name = res.data.name || ''
  } else {
    error.value = res.error || 'Could not load profile'
  }
  loading.value = false
}

const handleUpdate = async () => {
  updateError.value = ''
  if (updateForm.password && updateForm.password !== updateForm.password_confirmation) {
    updateError.value = 'Passwords do not match'
    return
  }

  updating.value = true
  const payload = { name: updateForm.name }
  if (updateForm.password) {
    if (!updateForm.old_password) {
      updateError.value = 'Please enter your current password'
      updating.value = false
      return
    }
    payload.old_password = updateForm.old_password
    payload.password = updateForm.password
    payload.password_confirmation = updateForm.password_confirmation
  }

  const res = await updateMyProfileApi(payload)
  if (res.success) {
    toaster.success('Profile updated successfully!')
    updateForm.password = ''
    updateForm.old_password = ''
    updateForm.password_confirmation = ''
    await fetchProfile()
  } else {
    updateError.value = res.error || 'Update failed'
  }
  updating.value = false
}

onMounted(fetchProfile)
</script>

<style scoped>
.avatar-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5c61f2, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.avatar-initials {
  color: white;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 1px;
}
.card { border-radius: 12px; }
.card-header { border-radius: 12px 12px 0 0 !important; padding: 1rem 1.5rem; }
.form-control { border-radius: 8px; }
.form-control:focus { border-color: #5c61f2; box-shadow: 0 0 0 0.2rem rgba(92, 97, 242, 0.15); }
.bg-primary-subtle { background-color: #ede9fe; }
</style>